import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  computed,
  inject,
  signal,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin } from 'rxjs';

import {
  HealthService,
  SimpleHealthResponse,
  FullHealthResponse,
  DatabaseHealthResponse,
  VersionResponse,
  HealthStatus,
  SystemResourcesResponse,
  ExternalServicesResponse,
} from '../../../../services/health.service';
import { ErrorLogService, ErrorLog, ErrorStatistics } from '../../../../services/error-log.service';

import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmBadge } from '@spartan-ng/helm/badge';
import { HlmButton } from '@spartan-ng/helm/button';
import { HlmTableImports } from '@spartan-ng/helm/table';
import { HlmSkeletonImports } from '@spartan-ng/helm/skeleton';
import { HlmDialogImports } from '@spartan-ng/helm/dialog';
import { HlmSelectImports } from '@spartan-ng/helm/select';
import { HlmLabel } from '@spartan-ng/helm/label';
import { HlmInput } from '@spartan-ng/helm/input';
import { BrnSelectImports } from '@spartan-ng/brain/select';
import { BrnDialogImports } from '@spartan-ng/brain/dialog';
import { FormsModule } from '@angular/forms';

// ---- Local interfaces ----

interface HealthCheckResult<T> {
  data: T | null;
  status: HealthStatus;
}

@Component({
  selector: 'app-health-tab',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './health-tab.component.html',
  imports: [
    DatePipe,
    FormsModule,
    ...HlmCardImports,
    HlmBadge,
    HlmButton,
    ...HlmTableImports,
    ...HlmSkeletonImports,
    ...HlmDialogImports,
    ...HlmSelectImports,
    ...BrnSelectImports,
    ...BrnDialogImports,
    HlmLabel,
    HlmInput,
  ],
})
export class HealthTabComponent implements OnInit, OnChanges, OnDestroy {
  @Input() autoRefresh = false;

  private readonly healthService = inject(HealthService);
  private readonly errorLogService = inject(ErrorLogService);
  private readonly destroyRef = inject(DestroyRef);

  // Health state
  readonly simpleHealth = signal<HealthCheckResult<SimpleHealthResponse> | null>(null);
  readonly fullHealth = signal<HealthCheckResult<FullHealthResponse> | null>(null);
  readonly databaseHealth = signal<HealthCheckResult<DatabaseHealthResponse> | null>(null);
  readonly versionInfo = signal<HealthCheckResult<VersionResponse> | null>(null);
  readonly systemResources = signal<SystemResourcesResponse | null>(null);
  readonly externalServices = signal<ExternalServicesResponse | null>(null);
  readonly isLoadingHealth = signal(false);
  readonly overallStatus = signal<'healthy' | 'degraded' | 'unhealthy'>('unhealthy');
  readonly lastUpdated = signal<Date>(new Date());

  // Computed health status booleans
  readonly isSimpleHealthy = computed(() => this.simpleHealth()?.status?.isHealthy ?? false);
  readonly isFullHealthy = computed(() => this.fullHealth()?.status?.isHealthy ?? false);
  readonly isDatabaseHealthy = computed(() => this.databaseHealth()?.status?.isHealthy ?? false);

  // Error log state
  readonly errorLogs = signal<ErrorLog[]>([]);
  readonly errorLogStatistics = signal<ErrorStatistics>({});
  readonly errorLogsLoading = signal(false);
  readonly selectedErrorLog = signal<ErrorLog | null>(null);
  readonly errorDetailOpen = signal(false);
  readonly errorLogPage = signal(1);
  readonly errorLogPageSize = 20;

  // Error log filter state (mutable plain object — drives ngModel)
  errorLogFilter = {
    count: 50,
    level: 'all',
    source: '',
    sinceHours: 24,
  };

  // Computed pagination
  readonly paginatedErrorLogs = computed(() => {
    const start = (this.errorLogPage() - 1) * this.errorLogPageSize;
    return this.errorLogs().slice(start, start + this.errorLogPageSize);
  });

  readonly totalErrorLogPages = computed(() =>
    Math.ceil(this.errorLogs().length / this.errorLogPageSize)
  );

  readonly errorLogPageNumbers = computed(() =>
    Array.from({ length: this.totalErrorLogPages() }, (_, i) => i + 1)
  );

  readonly statsEntries = computed(() =>
    Object.entries(this.errorLogStatistics())
  );

  // Computed resource percentages
  readonly memoryPercent = computed(() => {
    const res = this.systemResources();
    if (!res || res.memoryTotalMb === 0) return 0;
    return Math.round((res.memoryUsedMb / res.memoryTotalMb) * 100);
  });

  readonly diskPercent = computed(() => {
    const res = this.systemResources();
    if (!res || res.diskTotalGb === 0) return 0;
    return Math.round((res.diskUsedGb / res.diskTotalGb) * 100);
  });

  private refreshTimer?: ReturnType<typeof setInterval>;

  ngOnInit(): void {
    this.refreshAll();
    this.loadErrorLogs();
    this.loadErrorStatistics();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['autoRefresh']) {
      this.handleAutoRefreshChange();
    }
  }

  ngOnDestroy(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = undefined;
    }
  }

  private handleAutoRefreshChange(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = undefined;
    }
    if (this.autoRefresh) {
      this.refreshTimer = setInterval(() => {
        this.refreshAll();
        this.loadErrorLogs();
        this.loadErrorStatistics();
      }, 30000);
    }
  }

  refreshAll(): void {
    this.isLoadingHealth.set(true);

    this.healthService
      .getAllHealthChecks()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: results => {
          this.simpleHealth.set(results.simple as HealthCheckResult<SimpleHealthResponse>);
          this.fullHealth.set(results.full as HealthCheckResult<FullHealthResponse>);
          this.databaseHealth.set(results.database as HealthCheckResult<DatabaseHealthResponse>);
          this.versionInfo.set(results.version as HealthCheckResult<VersionResponse>);
          this.overallStatus.set(results.overallStatus);
          this.lastUpdated.set(new Date());
          this.isLoadingHealth.set(false);
        },
        error: () => {
          this.isLoadingHealth.set(false);
        },
      });

    // Load system resources and external services independently
    this.healthService
      .getSystemResources()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => this.systemResources.set(data),
        error: () => this.systemResources.set(null),
      });

    this.healthService
      .getExternalServices()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => this.externalServices.set(data),
        error: () => this.externalServices.set(null),
      });
  }

  loadErrorLogs(): void {
    this.errorLogsLoading.set(true);
    const level = this.errorLogFilter.level === 'all' ? undefined : this.errorLogFilter.level;
    const source = this.errorLogFilter.source.trim() || undefined;

    this.errorLogService
      .getRecentErrors(this.errorLogFilter.count, level, source)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: logs => {
          this.errorLogs.set(logs);
          this.errorLogsLoading.set(false);
        },
        error: () => {
          this.errorLogsLoading.set(false);
        },
      });
  }

  loadErrorStatistics(): void {
    const since = new Date();
    since.setHours(since.getHours() - this.errorLogFilter.sinceHours);

    this.errorLogService
      .getErrorStatistics(since)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: stats => this.errorLogStatistics.set(stats),
        error: () => {},
      });
  }

  applyErrorLogFilters(): void {
    this.errorLogPage.set(1);
    this.loadErrorLogs();
    this.loadErrorStatistics();
  }

  resetErrorLogFilters(): void {
    this.errorLogFilter = { count: 50, level: 'all', source: '', sinceHours: 24 };
    this.errorLogPage.set(1);
    this.loadErrorLogs();
    this.loadErrorStatistics();
  }

  showErrorDetails(log: ErrorLog): void {
    this.selectedErrorLog.set(log);
    this.errorDetailOpen.set(true);
  }

  closeErrorDetails(): void {
    this.errorDetailOpen.set(false);
    this.selectedErrorLog.set(null);
  }

  clearOldLogs(daysToKeep: number): void {
    if (!confirm(`Clear error logs older than ${daysToKeep} days? This cannot be undone.`)) return;

    this.errorLogService
      .clearOldLogs(daysToKeep)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => {
          alert(`Cleared ${result?.deletedCount ?? 0} old logs.`);
          this.loadErrorLogs();
          this.loadErrorStatistics();
        },
        error: () => alert('Failed to clear old logs.'),
      });
  }

  setPage(page: number): void {
    this.errorLogPage.set(page);
  }

  getOverallStatusLabel(status: string): string {
    switch (status) {
      case 'healthy': return 'All Systems Operational';
      case 'degraded': return 'Partial Service Degradation';
      default: return 'System Issues Detected';
    }
  }

  getLevelBadgeVariant(level: string): 'default' | 'destructive' | 'secondary' | 'outline' {
    switch (level.toLowerCase()) {
      case 'critical':
      case 'error': return 'destructive';
      case 'warning': return 'secondary';
      default: return 'outline';
    }
  }

  getStatusBadgeVariant(status: string): 'default' | 'destructive' | 'secondary' | 'outline' {
    switch (status) {
      case 'healthy': return 'default';
      case 'degraded': return 'secondary';
      default: return 'destructive';
    }
  }

  formatUptime(seconds: number): string {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return `${h}h ${m}m`;
  }
}
